/*
 * PDP V1 — one state model for the Product information (V1) section.
 *
 * Owns: option selection -> the form's single hidden variant id, price, CTA
 * state, the add-to-cart request (exactly one handler, on the form's submit
 * event), the mobile sticky bar, the size guide dialog, and "Purchase area"
 * tile links elsewhere on the page.
 *
 * Cart path matches assets/product-form.js: POST the whole form to the cart,
 * then open the Amp Slide Cart via SLIDECART_UPDATE/SLIDECART_OPEN, falling
 * back to /cart only if Amp never loads (CLAUDE.md, "Cart drawer").
 */
(function () {
  'use strict';

  var instances = [];
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 989px)');

  function qs(root, sel) { return root.querySelector(sel); }

  // Cents as money, in the shop's format, taken from a server-rendered sample
  // (e.g. "$128.00"): swap the sample's number for this amount.
  function formatMoney(cents, sample) {
    var amount = (cents / 100).toFixed(2);
    var m = /[\d.,]+/.exec(sample || '');
    if (!m) return '$' + amount;
    var usesComma = /,\d{2}$/.test(m[0]);
    var hasCents = /[.,]\d{2}$/.test(m[0]);
    var parts = amount.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, usesComma ? '.' : ',');
    var num = hasCents ? whole + (usesComma ? ',' : '.') + parts[1] : whole;
    return sample.replace(m[0], num);
  }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function headerOffset() {
    var total = 0;
    qsa(document, '.shopify-section-group-header-group, #shopify-section-header').forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        var r = el.getBoundingClientRect();
        if (r.bottom > 0) total = Math.max(total, r.bottom);
      }
    });
    return total + 12;
  }

  function scrollToEl(el) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  function isShown(el) {
    if (!el || !el.isConnected) return false;
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
  }

  // Anything that should take the bottom of the screen away from the sticky bar.
  var OVERLAY_SELECTORS = [
    '.slidecarthq-overlay',            // Amp cart drawer backdrop
    'dialog[open]',                    // size guide and any native dialog
    '.size-chart-popup.active',
    '.timesact_bis_popup_overlay',     // back-in-stock popup
    'csm-cookie-consent',              // consent banner
    'menu-drawer details[open] > .menu-drawer',
    '.menu-drawer-container[open]',
    '.drawer.active', '.modal.is-open'
  ];
  function overlayOpen() {
    for (var i = 0; i < OVERLAY_SELECTORS.length; i++) {
      var els = document.querySelectorAll(OVERLAY_SELECTORS[i]);
      for (var j = 0; j < els.length; j++) {
        if (els[j].closest('[data-pdp-v1-sticky]')) continue;
        if (OVERLAY_SELECTORS[i] === 'csm-cookie-consent') {
          // The consent host is a permanent, transparent full-screen layer;
          // only a visible banner inside its shadow root counts.
          var sr = els[j].shadowRoot;
          if (sr && Array.prototype.some.call(sr.querySelectorAll('*'), function (el) {
            var r = el.getBoundingClientRect();
            return r.height > 40 && r.width > 100 && isShown(el);
          })) return true;
          continue;
        }
        if (isShown(els[j])) return true;
      }
    }
    // Scroll lock by a menu or drawer.
    if (getComputedStyle(document.body).overflow === 'hidden') return true;
    return false;
  }

  function openCart() {
    var start = Date.now();
    (function tryOpen() {
      if (typeof window.SLIDECART_UPDATE === 'function' && typeof window.SLIDECART_OPEN === 'function') {
        window.SLIDECART_UPDATE(function () { window.SLIDECART_OPEN(); });
      } else if (Date.now() - start < 6000) {
        setTimeout(tryOpen, 150);
      } else {
        window.location = (window.routes && window.routes.cart_url) || '/cart';
      }
    })();
  }

  /* ---------- Simple Bundles picker (pdp-v1-bundle-picker.liquid) ---------- */

  // One "slot" per bundled product. Each slot's colour / size groups resolve to
  // one Simple Bundles choice string, written to properties[<slot name>]; all
  // choices together go in properties[_bundle_selection], joined by " <> ".
  function BundlePicker(el, owner) {
    this.el = el;
    this.owner = owner;
    var data = JSON.parse(qs(el, 'script[data-pdp-v1-bundle-data]').textContent);
    this.selectionInput = qs(el, '[data-bundle-selection]');
    var self = this;
    this.slots = data.slots.map(function (s, i) {
      var item = qs(el, '.pdp-v1__bundle-item[data-bundle-slot="' + i + '"]');
      var groups = qsa(item, '.pdp-v1__option');
      var slot = {
        name: s.name,
        values: s.values,
        item: item,
        groups: groups,
        input: qs(item, '[data-bundle-prop]'),
        thumb: qs(item, '[data-bundle-thumb]'),
        tile: document.querySelector('.pdp-v1__bundle-gallery [data-bundle-img="' + i + '"]'),
        colorIndex: -1,
        sizeIndex: -1,
        selected: [],
        choice: null
      };
      groups.forEach(function (g) {
        var k = parseInt(g.getAttribute('data-bundle-option'), 10);
        var kind = g.getAttribute('data-option-kind');
        if (kind === 'color') slot.colorIndex = k;
        else if (kind === 'size') slot.sizeIndex = k;
      });
      var n = s.values.length ? s.values[0].options.length : 1;
      for (var k = 0; k < n; k++) slot.selected.push(null);
      self.readSlot(slot);
      return slot;
    });

    owner.on(el, 'change', function (e) {
      var input = e.target;
      if (!input.matches('input[type=radio]')) return;
      var group = input.closest('.pdp-v1__option');
      var slot = self.slots[parseInt(group.getAttribute('data-bundle-slot'), 10)];
      var k = parseInt(group.getAttribute('data-bundle-option'), 10);
      var prev = slot.selected[k];
      slot.selected[k] = input.value;
      group.classList.remove('needs-choice');
      self.resolve(slot);
      if (k === slot.colorIndex && prev !== input.value) self.showImage(slot);
      owner.userTouched = true;
      owner.justAdded = false;
      clearTimeout(owner.addedTimer);
      owner.update();
    });
    this.render();
  }

  BundlePicker.prototype.readSlot = function (slot) {
    slot.groups.forEach(function (g) {
      var k = parseInt(g.getAttribute('data-bundle-option'), 10);
      var checked = qs(g, 'input[type=radio]:checked');
      slot.selected[k] = checked ? checked.value : null;
    });
    this.resolve(slot);
  };

  BundlePicker.prototype.resolve = function (slot) {
    var sel = slot.selected;
    var complete = sel.every(function (v) { return v !== null; });
    slot.choice = complete ? (slot.values.find(function (v) {
      return v.options.every(function (o, k) { return sel[k] === o; });
    }) || null) : null;
    if (slot.input) slot.input.value = slot.choice ? slot.choice.value : '';
  };

  // Best choice to represent a partial selection (for images).
  BundlePicker.prototype.representative = function (slot) {
    var sel = slot.selected;
    var matches = slot.values.filter(function (v) {
      return v.options.every(function (o, k) { return sel[k] === null || sel[k] === o; });
    });
    return matches.find(function (v) { return v.available; }) || matches[0] || null;
  };

  BundlePicker.prototype.showImage = function (slot) {
    var rep = slot.choice || this.representative(slot);
    if (!rep) return;
    if (slot.thumb && rep.thumb) slot.thumb.src = rep.thumb;
    if (slot.tile && rep.image) {
      slot.tile.removeAttribute('srcset');
      slot.tile.src = rep.image;
    }
  };

  BundlePicker.prototype.complete = function () {
    return this.slots.every(function (s) { return s.selected.every(function (v) { return v !== null; }); });
  };

  // Every slot resolves to a real, in-stock choice.
  BundlePicker.prototype.available = function () {
    return this.slots.every(function (s) { return s.choice && s.choice.available; });
  };

  BundlePicker.prototype.summary = function () {
    var self = this;
    // Leave out products with nothing to pick (no variants, e.g. the BroThrow).
    return this.slots.filter(function (s) {
      return !s.groups.length || !s.groups.every(function (g) { return g.hasAttribute('data-bundle-fixed'); });
    }).map(function (s) {
      return s.selected.every(function (v) { return v !== null; })
        ? s.selected.join(' ')
        : self.owner.strings.chooseSize;
    }).join(' · ');
  };

  BundlePicker.prototype.render = function () {
    var self = this;
    var strings = this.owner.strings;
    this.slots.forEach(function (slot) {
      slot.groups.forEach(function (g) {
        var k = parseInt(g.getAttribute('data-bundle-option'), 10);
        var lbl = qs(g, '[data-selected-label]');
        if (lbl) lbl.textContent = slot.selected[k] ? ': ' + slot.selected[k] : '';
        // Same rule as the product picker: a colour is crossed out only when it's
        // sold out in every size.
        var isColor = k === slot.colorIndex;
        qsa(g, 'input[type=radio]').forEach(function (input) {
          var trial = isColor ? slot.selected.map(function () { return null; }) : slot.selected.slice();
          trial[k] = input.value;
          var candidates = slot.values.filter(function (v) {
            return v.options.every(function (o, j) { return trial[j] === null || trial[j] === o; });
          });
          var exists = candidates.length > 0;
          var anyAvailable = candidates.some(function (v) { return v.available; });
          var label = input.nextElementSibling;
          if (!label) return;
          label.classList.toggle('is-soldout', exists && !anyAvailable);
          label.classList.toggle('is-unavailable', !exists);
          var note = qs(label, '[data-soldout-note]');
          if (note) note.textContent = !exists ? ' — ' + strings.unavailable : (!anyAvailable ? ' — ' + strings.soldOut : '');
        });
      });

      // Explain an unavailable pick in the slot's size group, never switch it.
      var msg = qs(slot.item, '[data-pdp-v1-size-msg]');
      if (!msg) return;
      var full = slot.selected.every(function (v) { return v !== null; });
      if (full && (!slot.choice || !slot.choice.available)) {
        var size = slot.sizeIndex > -1 ? slot.selected[slot.sizeIndex] : slot.selected.join(' ');
        msg.textContent = (slot.colorIndex > -1 ? strings.sizeUnavailable : strings.sizeSoldOut)
          .replace('[size]', size)
          .replace('[color]', slot.colorIndex > -1 ? slot.selected[slot.colorIndex] : '');
        msg.removeAttribute('data-guidance');
      } else if (!msg.hasAttribute('data-guidance') || full) {
        msg.textContent = '';
        msg.removeAttribute('data-guidance');
      }
    });

    if (this.selectionInput) {
      this.selectionInput.value = this.slots.every(function (s) { return s.choice; })
        ? this.slots.map(function (s) { return s.choice.value; }).join(' <> ')
        : '';
    }
  };

  BundlePicker.prototype.showGuidance = function () {
    var strings = this.owner.strings;
    for (var i = 0; i < this.slots.length; i++) {
      var slot = this.slots[i];
      var g = slot.groups.find(function (x) { return !qs(x, 'input:checked'); });
      if (!g) continue;
      var msg = qs(slot.item, '[data-pdp-v1-size-msg]');
      if (msg) {
        msg.textContent = g.getAttribute('data-option-kind') === 'size'
          ? (strings.sizeRequired || strings.chooseSize)
          : strings.chooseOption;
        msg.setAttribute('data-guidance', '');
      }
      g.classList.add('needs-choice');
      scrollToEl(g);
      var first = qs(g, 'input:not(:disabled)');
      if (first) first.focus({ preventScroll: true });
      return;
    }
  };

  /* ---------- Robe tiers: one row per robe (pdp-v1-robe-offer.liquid) ---------- */

  // After Duo / Trio is chosen, one row per robe inside that card, each with
  // small colour and size dropdowns (no expanding). Robe 1 is the main picker:
  // its dropdowns and the swatches above stay in step. Robes 2 and 3 start as
  // copies of robe 1. The add sends every robe as its own cart line.
  function MultiRobe(el, owner) {
    this.el = el;
    this.home = el.parentNode;
    this.owner = owner;
    this.rows = [];
    var self = this;
    owner.on(el, 'change', function (e) {
      var select = e.target;
      if (!select.matches('select[data-option-index]')) return;
      var row = self.rows[parseInt(select.closest('[data-robe-row]').getAttribute('data-robe-row'), 10)];
      var i = parseInt(select.getAttribute('data-option-index'), 10);
      if (row.index === 0) {
        // Robe 1 is the main picker: choose it there and let it update everything.
        var g = owner.groups.find(function (x) { return parseInt(x.getAttribute('data-option-index'), 10) === i; });
        var input = g && qsa(g, 'input[type=radio]').find(function (x) { return x.value === select.value; });
        if (input) input.click();
        return;
      }
      row.sel[i] = select.value;
      select.classList.remove('needs-choice');
      self.renderRow(row);
      owner.justAdded = false;
      owner.renderCta();
      owner.renderSticky();
    });
  }

  MultiRobe.prototype.active = function () { return this.rows.length > 1; };

  // n robes: keep rows that exist, copy the main selection into new ones.
  MultiRobe.prototype.setCount = function (n) {
    var owner = this.owner;
    if (n <= 1) {
      this.rows = [];
      this.el.innerHTML = '';
      this.el.hidden = true;
      return;
    }
    this.rows = this.rows.slice(0, n);
    for (var k = this.rows.length; k < n; k++) {
      this.rows.push({ index: k, sel: owner.selected.slice(), el: null });
    }
    this.el.innerHTML = '';
    var self = this;
    this.rows.forEach(function (row) { self.el.appendChild(self.buildRow(row)); self.renderRow(row); });
    this.el.hidden = false;
  };

  MultiRobe.prototype.buildRow = function (row) {
    var owner = this.owner;
    var k = row.index;
    var wrap = document.createElement('div');
    wrap.className = 'pdp-v1__robe-row';
    wrap.setAttribute('data-robe-row', k);
    var name = document.createElement('span');
    name.className = 'pdp-v1__robe-row-name';
    name.textContent = 'Robe ' + (k + 1);
    wrap.appendChild(name);
    var dot = document.createElement('span');
    dot.className = 'pdp-v1__robe-row-dot';
    dot.setAttribute('data-row-dot', '');
    dot.hidden = true;
    wrap.appendChild(dot);
    // One dropdown per option (colour, size), in the main picker's order.
    owner.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      var optName = owner.data.options[i] || '';
      var select = document.createElement('select');
      select.className = 'pdp-v1__robe-select pdp-v1__robe-select--' + (g.getAttribute('data-option-kind') || 'other');
      select.setAttribute('data-option-index', i);
      select.setAttribute('aria-label', 'Robe ' + (k + 1) + ' ' + optName.toLowerCase());
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = optName;
      placeholder.disabled = true;
      placeholder.setAttribute('data-placeholder', '');
      select.appendChild(placeholder);
      qsa(g, 'input[type=radio]').forEach(function (input) {
        var o = document.createElement('option');
        o.value = input.value;
        o.textContent = input.value;
        select.appendChild(o);
      });
      wrap.appendChild(select);
    });
    var msg = document.createElement('p');
    msg.className = 'pdp-v1__option-msg';
    msg.setAttribute('data-row-msg', '');
    msg.setAttribute('role', 'alert');
    wrap.appendChild(msg);
    row.el = wrap;
    return wrap;
  };

  // Robe 1 follows the main picker.
  MultiRobe.prototype.syncFirst = function () {
    if (!this.active()) return;
    this.rows[0].sel = this.owner.selected.slice();
    this.renderRow(this.rows[0]);
  };

  MultiRobe.prototype.rowVariant = function (row) {
    var owner = this.owner;
    if (!owner.groups.length) return owner.variant;
    return row.sel.every(function (v) { return v !== null; }) ? owner.findVariant(row.sel) : null;
  };

  MultiRobe.prototype.renderRow = function (row) {
    var owner = this.owner;
    if (!row.el) return;
    var parts = row.sel.filter(function (v) { return v !== null; });
    var missing = row.sel.some(function (v) { return v === null; });

    // Colour dot from the main picker's swatch for this colour.
    var dot = qs(row.el, '[data-row-dot]');
    if (dot) {
      var ci = owner.colorIndex;
      var swatchLabel = null;
      if (ci > -1 && row.sel[ci] !== null) {
        var g = owner.groups.find(function (x) { return parseInt(x.getAttribute('data-option-index'), 10) === ci; });
        var input = g && qsa(g, 'input[type=radio]').find(function (x) { return x.value === row.sel[ci]; });
        swatchLabel = input && input.nextElementSibling;
      }
      var color = swatchLabel ? swatchLabel.style.getPropertyValue('--swatch') : '';
      dot.hidden = !color;
      if (color) dot.style.setProperty('--swatch', color);
    }

    // Each dropdown shows the row's pick; sold-out choices are marked (same
    // rule as the main picker) but stay selectable, so the message can explain.
    qsa(row.el, 'select[data-option-index]').forEach(function (select) {
      var i = parseInt(select.getAttribute('data-option-index'), 10);
      var isColor = i === owner.colorIndex;
      qsa(select, 'option').forEach(function (o) {
        if (o.hasAttribute('data-placeholder')) return;
        var trial = isColor ? row.sel.map(function () { return null; }) : row.sel.slice();
        trial[i] = o.value;
        var candidates = owner.data.variants.filter(function (v) {
          return v.options.every(function (opt, j) { return trial[j] === null || trial[j] === opt; });
        });
        var anyAvailable = candidates.some(function (v) { return v.available; });
        o.textContent = o.value + (!candidates.length ? ' (' + owner.strings.unavailable.toLowerCase() + ')' : (!anyAvailable ? ' (' + owner.strings.soldOut.toLowerCase() + ')' : ''));
      });
      select.value = row.sel[i] !== null ? row.sel[i] : '';
    });

    var msg = qs(row.el, '[data-row-msg]');
    if (msg && row.index > 0) {
      var v = this.rowVariant(row);
      msg.textContent = !missing && (!v || !v.available)
        ? 'Robe ' + (row.index + 1) + ': ' + parts.join(' · ') + ' is ' + (v ? owner.strings.soldOut : owner.strings.unavailable).toLowerCase() + '. Please change it.'
        : '';
    }
  };

  MultiRobe.prototype.complete = function () {
    return this.rows.every(function (r) { return r.sel.every(function (v) { return v !== null; }); });
  };

  // Variants to add, one per robe (null where a row has no real variant).
  MultiRobe.prototype.variants = function () {
    var self = this;
    return this.rows.map(function (r, k) { return k === 0 ? self.owner.variant : self.rowVariant(r); });
  };

  MultiRobe.prototype.buyable = function () {
    return this.variants().every(function (v) { return v && v.available; });
  };

  MultiRobe.prototype.summary = function () {
    return this.rows.map(function (r) { return r.sel.filter(function (v) { return v !== null; }).join(' '); }).join(' · ');
  };

  MultiRobe.prototype.showGuidance = function () {
    for (var k = 1; k < this.rows.length; k++) {
      var row = this.rows[k];
      if (row.sel.every(function (v) { return v !== null; })) continue;
      var select = qsa(row.el, 'select[data-option-index]').find(function (x) { return !x.value; });
      if (select) { select.classList.add('needs-choice'); scrollToEl(select); select.focus({ preventScroll: true }); }
      return;
    }
  };

  function PdpV1(root) {
    this.root = root;
    this.cleanup = [];
    var dataEl = qs(root, 'script[data-pdp-v1-product]');
    this.data = JSON.parse(dataEl.textContent);
    // Liquid's t filter HTML-escapes (e.g. isn&#39;t); decode for textContent.
    var decoder = document.createElement('textarea');
    this.strings = {};
    for (var k in this.data.strings) { decoder.innerHTML = this.data.strings[k]; this.strings[k] = decoder.value; }
    this.form = document.getElementById(root.getAttribute('data-form-id'));
    this.idInput = this.form && qs(this.form, '[data-pdp-v1-id]');
    this.cta = this.form && qs(this.form, '[data-pdp-v1-cta]');
    this.ctaLabel = this.cta && qs(this.cta, '[data-pdp-v1-label]');
    this.msg = this.form && qs(this.form, '[data-pdp-v1-msg]');
    this.defaultLabel = (this.cta && this.cta.getAttribute('data-default-label')) || this.strings.addToCart;
    this.picker = qs(root, '[data-pdp-v1-picker]');
    this.termsId = qs(root, '[data-pdp-v1-terms-id]');
    this.express = this.form && qs(this.form, '[data-pdp-v1-express]');
    this.recipient = this.form && qs(this.form, '[data-pdp-v1-recipient]');
    this.priceEl = qs(root, '[data-pdp-v1-price]');
    this.sticky = qs(root, '[data-pdp-v1-sticky]');
    this.pending = false;
    this.userTouched = false;
    this.timers = [];

    this.groups = this.picker ? qsa(this.picker, '[data-option-index]').filter(function (el) {
      return el.classList.contains('pdp-v1__option');
    }) : [];
    this.sizeIndex = -1;
    this.colorIndex = -1;
    this.otherIndexes = [];
    var self = this;
    this.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      var kind = g.getAttribute('data-option-kind');
      if (kind === 'size') self.sizeIndex = i;
      else if (kind === 'color') self.colorIndex = i;
      else self.otherIndexes.push(i);
    });
    // "Choose a size" only where there is a size to choose.
    this.chooseLabel = this.sizeIndex > -1 ? this.strings.chooseSize : this.strings.chooseOption;

    // Simple Bundles product: the bundle has one variant; its picks travel as
    // line item properties (BundlePicker).
    var bundleEl = qs(root, '[data-pdp-v1-bundle]');
    this.bundle = bundleEl ? new BundlePicker(bundleEl, this) : null;
    if (this.bundle) this.chooseLabel = this.strings.chooseSize;

    // Robe tiers (robe-tiers.js): promo tiles set the quantity; the sticky
    // button offers "Add another robe" once the cart holds qualifying robes.
    this.tierEligible = !!(this.data.robeTiers && this.data.robeTiers.eligible);
    this.tierCount = window.RobeTiers ? window.RobeTiers.count : null;
    this.tiles = qsa(root, '[data-robe-tile]');
    this.qtyInput = this.form && qs(this.form, 'input[name="quantity"]');
    this.qty = 1;
    var multiEl = qs(root, '[data-robe-multi]');
    this.multi = multiEl && this.tiles.length ? new MultiRobe(multiEl, this) : null;

    this.applySizeParam();
    this.selected = this.readSelection();
    this.bind();
    this.setupRecipient();
    this.watchEmptyAppBlocks();
    this.update({ initial: true });
    this.setupSticky();
    this.setupGuide();
  }

  // App blocks (e.g. Kaching Bundles with no active deal) render an empty
  // wrapper that still takes a flex gap. Cancel the gap while it is empty;
  // restore it automatically if the app fills it later.
  PdpV1.prototype.watchEmptyAppBlocks = function () {
    if (!('ResizeObserver' in window)) return;
    var self = this;
    var ro = new ResizeObserver(function (entries) {
      entries.forEach(function (en) {
        en.target.classList.toggle('pdp-v1-empty', en.contentRect.height < 1);
      });
    });
    qsa(this.root, '.pdp-v1__buy > .blocks-section').forEach(function (el) { ro.observe(el); });
    this.cleanup.push(function () { ro.disconnect(); });
  };

  PdpV1.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.cleanup.push(function () { target.removeEventListener(type, fn, opts); });
  };

  // Arriving from another robe's "Other styles" link (?size=L-XL): preselect
  // that size if this product has it. A ?color= link (a Limited Edition or
  // core colour swatch, e.g. Desert Camo) picks that colour the same way, so
  // no size gets chosen for the shopper.
  PdpV1.prototype.applySizeParam = function () {
    var params = new URLSearchParams(window.location.search);
    var self = this;
    [['size', params.get('size')], ['color', params.get('color')]].forEach(function (pair) {
      if (!pair[1]) return;
      var g = self.groups.find(function (x) { return x.getAttribute('data-option-kind') === pair[0]; });
      var input = g && qsa(g, 'input[type=radio]').find(function (x) { return x.value === pair[1]; });
      if (input) input.checked = true;
    });
  };

  PdpV1.prototype.readSelection = function () {
    var sel = [];
    for (var i = 0; i < this.data.options.length; i++) sel.push(null);
    this.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      var checked = qs(g, 'input[type=radio]:checked');
      sel[i] = checked ? checked.value : null;
    });
    // No V1 picker on the page (removed in the editor, or a single-variant
    // product): use the form's server-rendered variant, else the first available,
    // so the button always works.
    if (!this.groups.length) {
      var idNow = this.idInput && this.idInput.value;
      var v = this.data.variants.find(function (x) { return String(x.id) === String(idNow); }) ||
        this.data.variants.find(function (x) { return x.available; }) || this.data.variants[0];
      return v ? v.options.slice() : sel;
    }
    return sel;
  };

  PdpV1.prototype.complete = function () {
    return this.selected.every(function (v) { return v !== null; }) &&
      (!this.bundle || this.bundle.complete()) &&
      (!this.multi || !this.multi.active() || this.multi.complete());
  };

  PdpV1.prototype.findVariant = function (sel) {
    return this.data.variants.find(function (v) {
      return v.options.every(function (o, i) { return sel[i] === o; });
    }) || null;
  };

  // Best variant to represent a partial selection (for price and gallery).
  PdpV1.prototype.representative = function () {
    var sel = this.selected;
    var matches = this.data.variants.filter(function (v) {
      return v.options.every(function (o, i) { return sel[i] === null || sel[i] === o; });
    });
    return matches.find(function (v) { return v.available; }) || matches[0] || this.data.variants[0];
  };

  PdpV1.prototype.bind = function () {
    var self = this;
    if (this.picker) {
      // "More styles" links carry the chosen size to the other robe's page.
      this.on(this.picker, 'click', function (e) {
        var link = e.target.closest('[data-more-style]');
        if (!link || self.sizeIndex < 0 || !self.selected[self.sizeIndex]) return;
        var url = new URL(link.href, window.location.origin);
        url.searchParams.set('size', self.selected[self.sizeIndex]);
        link.href = url.pathname + url.search;
      });
      this.on(this.picker, 'change', function (e) {
        var input = e.target;
        if (!input.matches('input[type=radio]')) return;
        var i = parseInt(input.getAttribute('data-option-index'), 10);
        var prev = self.selected[i];
        self.selected[i] = input.value;
        self.userTouched = true;
        self.justAdded = false;
        clearTimeout(self.addedTimer);
        self.update({ changedIndex: i, colorChanged: i === self.colorIndex && prev !== input.value });
      });
    }
    if (this.cta) {
      this.on(this.cta, 'click', function (e) { e.preventDefault(); self.submit(); });
    }
    this.tiles.forEach(function (tile) {
      self.on(tile, 'change', function () {
        if (tile.checked) self.setQty(parseInt(tile.getAttribute('data-qty'), 10) || 1);
      });
    });
    if (this.tierEligible) {
      this.on(document, 'robe-tiers:update', function (e) {
        self.tierCount = e.detail ? e.detail.count : null;
        self.renderSticky();
      });
    }
    if (this.form) {
      // Enter-key or programmatic submits: keep Amp's document-level submit
      // hook out of it, so the add happens exactly once, here.
      this.on(this.form, 'submit', function (e) { e.preventDefault(); e.stopPropagation(); self.submit(); });
    }
  };

  // Express checkout buys exactly one variant and skips the cart, so it hides
  // for bundles (their picks travel as cart properties) and while several robes
  // are lined up in the tier rows.
  PdpV1.prototype.renderExpress = function () {
    if (!this.express) return;
    this.express.hidden = !!this.bundle || !!(this.multi && this.multi.active()) ||
      !(this.variant && this.variant.available);
  };

  // Quantity for the next add (robe pack cards); 1 unless Duo / Trio is chosen.
  PdpV1.prototype.setQty = function (q) {
    this.qty = q;
    // With robe rows, each robe goes as its own line; the form keeps 1.
    if (this.qtyInput) this.qtyInput.value = this.multi ? 1 : q;
    if (this.multi) this.multi.setCount(q);
    this.renderExpress();
    var self = this;
    // Single: the (now empty) rows go back under the cards.
    if (this.multi && q <= 1 && this.multi.home && this.multi.el.parentNode !== this.multi.home) {
      this.multi.home.appendChild(this.multi.el);
    }
    this.tiles.forEach(function (tile) {
      var on = parseInt(tile.getAttribute('data-qty'), 10) === q;
      tile.checked = on;
      var card = tile.closest('[data-robe-pack]');
      if (!card) return;
      card.classList.toggle('is-selected', on);
      // The robe rows sit inside the chosen card.
      var slot = qs(card, '[data-robe-pack-slot]');
      if (on && slot && self.multi && self.multi.el.parentNode !== slot) slot.appendChild(self.multi.el);
    });
    this.justAdded = false;
    this.renderCta();
    this.renderSticky();
  };

  // Each pack card's total (and struck-through full price) follows the
  // selected variant.
  PdpV1.prototype.renderTiles = function (rep) {
    if (!this.tiles.length || !rep || !window.RobeTiers) return;
    var RT = window.RobeTiers;
    var promo = RT.cfg.promoOn ? RT.cfg.promoPct : 0;
    var base = rep.basePrice != null ? rep.basePrice : rep.price;
    this.tiles.forEach(function (tile) {
      var card = tile.closest('[data-robe-pack]');
      var price = card && qs(card, '[data-pack-price]');
      if (!price) return;
      var q = parseInt(tile.getAttribute('data-qty'), 10) || 1;
      price.textContent = formatMoney(q * RT.unitPrice(base, parseFloat(tile.getAttribute('data-tier-pct')), promo), rep.priceHtml);
      var compare = qs(card, '[data-pack-compare]');
      if (compare) compare.textContent = formatMoney(q * base, rep.priceHtml);
    });
  };

  /* ---------- Gift card recipient ---------- */

  // Fields stay disabled (never submitted) until "send as a gift" is ticked.
  PdpV1.prototype.setupRecipient = function () {
    var r = this.recipient;
    if (!r) return;
    var toggle = qs(r, '[data-recipient-toggle]');
    var fields = qs(r, '[data-recipient-fields]');
    var offset = qs(r, '[data-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());
    var sendOn = qs(r, '[data-recipient-sendon]');
    if (sendOn) {
      // Shopify accepts a send date from today up to 90 days out.
      var d = new Date();
      var iso = function (x) { return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
      sendOn.min = iso(d);
      sendOn.max = iso(new Date(d.getTime() + 90 * 86400000));
    }
    var sync = function () {
      var on = toggle.checked;
      fields.hidden = !on;
      qsa(fields, 'input, textarea').forEach(function (el) { el.disabled = !on; });
    };
    this.on(toggle, 'change', sync);
    sync();
  };

  // Returns false (and says why) when the recipient email is missing or invalid.
  PdpV1.prototype.recipientValid = function () {
    var r = this.recipient;
    if (!r) return true;
    var toggle = qs(r, '[data-recipient-toggle]');
    if (!toggle || !toggle.checked) return true;
    var email = qs(r, '[data-recipient-email]');
    if (email && email.value.trim() && email.checkValidity()) return true;
    this.setMessage(this.strings.recipientEmail || 'Please enter a valid recipient email.', true);
    if (email) { email.setAttribute('aria-invalid', 'true'); email.focus(); }
    return false;
  };

  PdpV1.prototype.update = function (opts) {
    opts = opts || {};
    var self = this;
    var variant = this.complete() ? this.findVariant(this.selected) : null;
    this.variant = variant;
    var rep = variant || this.representative();

    if (this.idInput) this.idInput.value = variant ? variant.id : '';
    // Instalment message: follows the chosen variant, or the representative one
    // before a size is picked, so the amount always matches the price shown.
    if (this.termsId) {
      var termsVariant = variant || rep;
      if (termsVariant && String(this.termsId.value) !== String(termsVariant.id)) {
        this.termsId.value = termsVariant.id;
        var terms = qs(this.root, 'shopify-payment-terms');
        if (terms) terms.setAttribute('variant-id', termsVariant.id);
      }
    }
    // Native express checkout: only once a real, buyable variant is chosen.
    this.renderExpress();
    if (this.bundle) this.bundle.render();

    // Selected-value labels next to each option name.
    this.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      var lbl = qs(g, '[data-selected-label]');
      if (lbl) lbl.textContent = self.selected[i] ? ': ' + self.selected[i] : '';
    });

    this.markAvailability();

    // Price: the chosen variant, or the representative of a partial choice.
    if (this.priceEl) {
      var cur = qs(this.priceEl, '[data-price]');
      var cmpWrap = qs(this.priceEl, '[data-compare-wrap]');
      var cmp = qs(this.priceEl, '[data-compare]');
      var onSale = rep.compare_at_price > rep.price;
      if (cur) { cur.innerHTML = rep.priceHtml; cur.classList.toggle('is-sale', onSale); }
      if (cmpWrap) cmpWrap.hidden = !onSale;
      if (cmp) cmp.innerHTML = onSale ? rep.compareHtml : '';
    }

    // Size message: explain, never silently switch the shopper's size.
    var sizeMsg = this.picker && qs(this.picker, '[data-pdp-v1-size-msg]');
    if (sizeMsg) {
      if (this.complete() && (!variant || !variant.available) && this.sizeIndex > -1) {
        // Size-only products (e.g. The Hef) have no colour to name.
        sizeMsg.textContent = (this.colorIndex > -1 ? this.strings.sizeUnavailable : this.strings.sizeSoldOut)
          .replace('[size]', this.selected[this.sizeIndex])
          .replace('[color]', this.colorIndex > -1 ? this.selected[this.colorIndex] : '');
      } else if (!sizeMsg.hasAttribute('data-guidance') || this.selected[this.sizeIndex]) {
        sizeMsg.textContent = '';
        sizeMsg.removeAttribute('data-guidance');
      }
    }

    this.renderTiles(rep);
    if (this.multi) this.multi.syncFirst();
    this.renderCta();

    // Gallery follows a deliberate colour change.
    if (opts.colorChanged && rep && rep.mediaId) {
      document.dispatchEvent(new CustomEvent('variant:imageChanged', {
        detail: { variantId: rep.id, mediaId: rep.mediaId }
      }));
    }

    // Arriving on a ?variant= or ?color= link (e.g. Desert Camo from another
    // robe's Other Styles row): open the gallery on that colour's photo. The gallery
    // (product-gallery.js) starts on its first photo at DOMContentLoaded, so
    // send this once every DOMContentLoaded handler has run.
    if (opts.initial && rep && rep.mediaId && /[?&](variant|color)=/.test(window.location.search)) {
      var urlMedia = { variantId: rep.id, mediaId: rep.mediaId };
      var showUrlMedia = function () {
        setTimeout(function () {
          document.dispatchEvent(new CustomEvent('variant:imageChanged', { detail: urlMedia }));
        }, 0);
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', showUrlMedia);
      else showUrlMedia();
    }

    // A complete deliberate choice becomes shareable and survives reload/back.
    if (!opts.initial && variant && !this.bundle && window.history && history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      url.searchParams.delete('color'); // the variant names the colour now
      history.replaceState(history.state, '', url.toString());
    }

    this.renderSticky();
  };

  PdpV1.prototype.markAvailability = function () {
    var self = this;
    this.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      // A colour is crossed out only when it's sold out in every size; the
      // size chips (and the size message) say which sizes a colour lacks.
      var isColor = i === self.colorIndex;
      qsa(g, 'input[type=radio]').forEach(function (input) {
        var trial = isColor ? self.selected.map(function () { return null; }) : self.selected.slice();
        trial[i] = input.value;
        var candidates = self.data.variants.filter(function (v) {
          return v.options.every(function (o, k) { return trial[k] === null || trial[k] === o; });
        });
        var exists = candidates.length > 0;
        var anyAvailable = candidates.some(function (v) { return v.available; });
        var label = input.nextElementSibling;
        if (!label) return;
        label.classList.toggle('is-soldout', exists && !anyAvailable);
        label.classList.toggle('is-unavailable', !exists);
        var note = qs(label, '[data-soldout-note]');
        if (note) note.textContent = !exists ? ' — ' + self.strings.unavailable : (!anyAvailable ? ' — ' + self.strings.soldOut : '');
      });
    });
  };

  PdpV1.prototype.ctaState = function () {
    if (this.pending) return { label: this.strings.adding, disabled: true, busy: true };
    if (!this.complete()) {
      return { label: this.defaultLabel, disabled: false, needsSize: true };
    }
    if (!this.variant) return { label: this.strings.unavailable, disabled: true };
    if (!this.variant.available) return { label: this.strings.soldOut, disabled: true };
    if (this.bundle && !this.bundle.available()) return { label: this.strings.soldOut, disabled: true };
    if (this.multi && this.multi.active()) {
      if (!this.multi.buyable()) return { label: this.strings.soldOut, disabled: true };
      return { label: 'Add ' + this.qty + ' robes · ' + this.multiTotal(), disabled: false };
    }
    if (this.qty > 1) return { label: 'Add ' + this.qty + ' robes', disabled: false };
    return { label: this.defaultLabel, disabled: false };
  };

  PdpV1.prototype.renderCta = function () {
    if (!this.cta) return;
    var st = this.ctaState();
    this.ctaLabel.textContent = this.justAdded ? this.strings.added : st.label;
    // Pending uses aria-disabled so focus stays on the button.
    this.cta.disabled = !!st.disabled && !st.busy;
    if (st.busy) this.cta.setAttribute('aria-disabled', 'true'); else this.cta.removeAttribute('aria-disabled');
    this.cta.classList.toggle('is-loading', !!st.busy);
  };

  // What the robes in the rows cost with the pressed tile's tier (and any promo).
  PdpV1.prototype.multiTotal = function () {
    var RT = window.RobeTiers;
    var tile = this.tiles.find(function (t) { return t.checked; });
    var tierPct = tile ? parseFloat(tile.getAttribute('data-tier-pct')) : 0;
    var promo = RT && RT.cfg.promoOn ? RT.cfg.promoPct : 0;
    var sample = (this.variant || this.data.variants[0]).priceHtml;
    var total = this.multi.variants().reduce(function (sum, v) {
      var base = v.basePrice != null ? v.basePrice : v.price;
      return sum + (RT ? RT.unitPrice(base, tierPct, promo) : base);
    }, 0);
    return formatMoney(total, sample);
  };

  PdpV1.prototype.showSizeGuidance = function () {
    if (this.bundle && !this.bundle.complete()) { this.bundle.showGuidance(); return; }
    if (this.multi && this.multi.active() && this.selected.every(function (v) { return v !== null; })) {
      this.multi.showGuidance();
      return;
    }
    var g = this.groups.find(function (x) { return x.getAttribute('data-option-kind') === 'size'; }) ||
      this.groups.find(function (x) { return !qs(x, 'input:checked'); });
    if (!g) return;
    var msg = qs(g, '[data-pdp-v1-size-msg]');
    if (msg) {
      msg.textContent = g.getAttribute('data-option-kind') === 'size'
        ? (this.strings.sizeRequired || this.strings.chooseSize)
        : this.strings.chooseOption;
      msg.setAttribute('data-guidance', '');
    }
    g.classList.add('needs-choice');
    scrollToEl(g);
    var first = qs(g, 'input:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  };

  PdpV1.prototype.submit = function () {
    if (this.pending) return;
    if (!this.complete()) { this.showSizeGuidance(); return; }
    if (!this.variant || !this.variant.available) return;
    if (this.bundle && !this.bundle.available()) return;
    var multi = this.multi && this.multi.active();
    if (multi && !this.multi.buyable()) return;
    if (!this.recipientValid()) return;

    var self = this;
    var submitted = this.variant;
    this.pending = true;
    this.setMessage('');
    this.renderCta();
    this.renderSticky();

    var headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };
    var body = new FormData(this.form);
    if (multi) {
      // One line per robe (same choices merge), carrying the form's properties.
      var props = {};
      body.forEach(function (value, key) {
        var m = /^properties\[(.+)\]$/.exec(key);
        if (m && value !== '') props[m[1]] = value;
      });
      var lines = [];
      this.multi.variants().forEach(function (v) {
        var line = lines.find(function (l) { return l.id === v.id; });
        if (line) line.quantity += 1;
        else lines.push({ id: v.id, quantity: 1, properties: props });
      });
      body = JSON.stringify({ items: lines });
      headers['Content-Type'] = 'application/json';
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: headers,
      body: body
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (json) {
          if (!res.ok || json.status) {
            // description is a string, except for field errors (e.g. a gift card
            // recipient email), where it is an object; message is always a string.
            var desc = typeof json.description === 'string' ? json.description : '';
            throw new Error(desc || json.message || self.strings.error);
          }
          return json;
        });
      })
      .then(function (item) {
        if (self.destroyed) return;
        self.pending = false;
        self.justAdded = true;
        if (self.qty !== 1) self.setQty(1);
        self.justAdded = true;
        self.renderCta();
        clearTimeout(self.addedTimer);
        self.addedTimer = setTimeout(function () { self.justAdded = false; self.renderCta(); self.renderSticky(); }, 2000);
        // PUB_SUB_EVENTS and publish are top-level declarations in constants.js /
        // pubsub.js (not window properties), so reference them by name.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: submitted.id, cartData: item });
          }
        } catch (err) {}
        openCart();
      })
      .catch(function (err) {
        if (self.destroyed) return;
        self.pending = false;
        self.renderCta();
        self.setMessage(err && err.message && err.message !== 'Failed to fetch' ? err.message : self.strings.error, true);
      })
      .then(function () { if (!self.destroyed) self.renderSticky(); });
  };

  PdpV1.prototype.setMessage = function (text, isError) {
    if (!this.msg) return;
    this.msg.textContent = text || '';
    this.msg.classList.toggle('is-error', !!isError);
  };

  /* ---------- Sticky bar (mobile) ---------- */

  PdpV1.prototype.setupSticky = function () {
    if (!this.sticky || !this.cta) return;
    var self = this;
    this.stickyBtn = qs(this.sticky, '[data-pdp-v1-sticky-cta]');
    this.stickyLabel = qs(this.sticky, '[data-sticky-label]');
    this.stickyPrice = qs(this.sticky, '[data-sticky-price]');
    this.stickySummary = qs(this.sticky, '[data-sticky-summary]');
    this.ctaPassed = false;
    this.inputFocused = false;

    // Scroll-position check rather than IntersectionObserver: an observer only
    // reports changes in intersection, so a jump from below the fold straight
    // past the button (fast fling, anchor link) never registers.
    var target = this.cta.closest('.pdp-v1__atc') || this.cta;
    this.measureCta = function () {
      var bottom = target.getBoundingClientRect().bottom;
      // Hysteresis: show once 8px past, hide once back in view.
      if (bottom < -8) self.ctaPassed = true;
      else if (bottom > 8) self.ctaPassed = false;
    };

    this.on(this.stickyBtn, 'click', function () {
      if (!self.complete()) { self.showSizeGuidance(); return; }
      if (self.variant && self.variant.available && !self.pending) self.submit();
    });

    var raf = 0;
    var schedule = function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; self.measureCta(); self.refreshSticky(); });
    };
    this.on(window, 'scroll', schedule, { passive: true });
    // Overlays (cart drawer, menu, dialogs, popups) open in response to a click
    // or key, or on load (consent, pop-ups). Re-check after those moments rather
    // than observing the whole document for mutations.
    var later = function () {
      schedule();
      [150, 450, 1000].forEach(function (ms) { self.timers.push(setTimeout(schedule, ms)); });
    };
    this.on(document, 'click', later, true);
    this.on(document, 'keyup', later, true);
    this.on(document, 'transitionend', schedule, true);
    this.on(window, 'load', later);
    [1500, 4000].forEach(function (ms) { self.timers.push(setTimeout(schedule, ms)); });
    this.on(document, 'focusin', function (e) {
      self.inputFocused = !!(e.target && e.target.matches && e.target.matches('input[type=text], input[type=email], input[type=tel], input:not([type]), textarea, [contenteditable=true]'));
      schedule();
    });
    this.on(document, 'focusout', function () { self.inputFocused = false; schedule(); });
    this.on(window, 'resize', schedule);
    this.on(window, 'pageshow', schedule);
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', schedule);
      this.cleanup.push(function () { mobileQuery.removeEventListener('change', schedule); });
    }
    this.measureCta();
    this.renderSticky();
    this.refreshSticky();
  };

  PdpV1.prototype.refreshSticky = function () {
    if (!this.sticky) return;
    var show = mobileQuery.matches && this.ctaPassed && !this.inputFocused && !overlayOpen();
    if (this.sticky.hidden === !show) return;
    this.sticky.hidden = !show;
    document.documentElement.classList.toggle('pdp-v1-sticky-visible', show);
  };

  PdpV1.prototype.renderSticky = function () {
    if (!this.sticky || !this.stickyBtn) { return; }
    var rep = this.variant || this.representative();
    if (this.stickyPrice) this.stickyPrice.innerHTML = rep.priceHtml;
    var parts = [];
    var self = this;
    if (this.colorIndex > -1 && this.selected[this.colorIndex]) parts.push(this.selected[this.colorIndex]);
    if (this.sizeIndex > -1) parts.push(this.selected[this.sizeIndex] || this.strings.chooseSize);
    this.otherIndexes.forEach(function (i) { if (self.selected[i]) parts.push(self.selected[i]); });
    if (this.bundle) parts = [this.bundle.summary()];
    if (this.multi && this.multi.active()) parts = [this.multi.summary()];
    if (this.stickySummary) this.stickySummary.textContent = parts.join(' · ');
    var st = this.ctaState();
    var label = this.justAdded ? this.strings.added : (st.needsSize ? this.chooseLabel : st.label);
    if (!this.justAdded && !st.needsSize && !st.disabled && this.qty === 1 && this.tierEligible && this.tierCount > 0) {
      label = 'Add another robe';
    }
    this.stickyLabel.textContent = label;
    this.stickyBtn.disabled = !!st.disabled && !st.busy;
    if (st.busy) this.stickyBtn.setAttribute('aria-disabled', 'true'); else this.stickyBtn.removeAttribute('aria-disabled');
    this.stickyBtn.classList.toggle('is-loading', !!st.busy);
  };

  /* ---------- Size guide dialog ---------- */

  PdpV1.prototype.setupGuide = function () {
    var dlg = qs(this.root, '[data-pdp-v1-guide]');
    var opener = qs(this.root, '[data-pdp-v1-guide-open]');
    if (!dlg || !opener || typeof dlg.showModal !== 'function') return;
    var self = this;
    var lastFocus = null;
    this.on(opener, 'click', function () { lastFocus = opener; dlg.showModal(); });
    qsa(dlg, '[data-pdp-v1-guide-close]').forEach(function (b) {
      self.on(b, 'click', function () { dlg.close(); });
    });
    // Click on the backdrop closes.
    this.on(dlg, 'click', function (e) { if (e.target === dlg) dlg.close(); });
    this.on(dlg, 'close', function () { if (lastFocus) lastFocus.focus({ preventScroll: true }); });
  };

  PdpV1.prototype.destroy = function () {
    this.destroyed = true;
    clearTimeout(this.addedTimer);
    this.timers.forEach(clearTimeout);
    this.cleanup.forEach(function (fn) { try { fn(); } catch (e) {} });
    this.cleanup = [];
    document.documentElement.classList.remove('pdp-v1-sticky-visible');
    this.root.__pdpV1 = null;
  };

  /* ---------- Page-level wiring ---------- */

  function initAll(scope) {
    qsa(scope || document, '[data-pdp-v1]').forEach(function (root) {
      if (root.__pdpV1) return;
      try {
        root.__pdpV1 = new PdpV1(root);
        instances.push(root.__pdpV1);
      } catch (e) {
        if (window.console) console.error('[pdp-v1]', e);
      }
    });
  }

  if (window.__pdpV1Wired) { initAll(); return; }
  window.__pdpV1Wired = true;

  /* ---------- Style swap ---------- */
  // A Limited Edition swatch (or colour sibling) whose product uses this
  // template carries data-style-swap (pdp-v1-more-styles.liquid). Clicking it
  // swaps that product into the page like picking a variant: this section is
  // re-rendered for it (Section Rendering API) and the URL changes, so
  // refresh, share and Back all work. The styles row from the page the
  // shopper landed on stays put, with a swatch for that product as the way
  // back. Anything unexpected falls back to a normal page load.
  var swapCache = {};
  var swapping = false;
  var familyRow = null; // the landing page's styles row, kept across swaps
  var currentHandle = null;
  var ringHandle = null; // whose swatch in the row is ringed

  function readJson(root, sel) {
    var el = qs(root, sel);
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  }

  function handleFromPath(pathname) {
    var m = /\/products\/([^/?#]+)/.exec(pathname);
    return m ? decodeURIComponent(m[1]) : null;
  }

  // The section HTML for a product page URL. Only ?variant= changes what the
  // server renders (?size= is applied here), so it alone is part of the key.
  function fetchSection(url, sectionId) {
    var key = url.pathname + '?' + (url.searchParams.get('variant') ? 'variant=' + encodeURIComponent(url.searchParams.get('variant')) + '&' : '') +
      'section_id=' + encodeURIComponent(sectionId);
    if (!swapCache[key]) {
      swapCache[key] = fetch(key, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('section ' + r.status);
        return r.text();
      });
      swapCache[key].catch(function () { delete swapCache[key]; });
    }
    return swapCache[key];
  }

  // The new product's root, or null when it can't be swapped in here.
  function parseSection(html, sectionId) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var root = doc.querySelector('[data-pdp-v1]');
    if (!root || root.getAttribute('data-section-id') !== sectionId) return null;
    // Robe tiers' script only loads on tier-eligible pages (theme.liquid).
    var data = readJson(root, '[data-pdp-v1-product]');
    if (!data || (data.robeTiers && data.robeTiers.eligible && !window.RobeTiers)) return null;
    // Data blocks stay; script tags were already run by the first page load.
    qsa(root, 'script').forEach(function (s) {
      if (!/json/i.test(s.getAttribute('type') || '')) s.remove();
    });
    return document.importNode(root, true);
  }

  function linkUrl(link) {
    var url = new URL(link.href, window.location.origin);
    var root = link.closest('[data-pdp-v1]');
    var inst = root && root.__pdpV1;
    if (inst && inst.sizeIndex >= 0 && inst.selected[inst.sizeIndex]) url.searchParams.set('size', inst.selected[inst.sizeIndex]);
    return url;
  }

  // The landing page's styles row goes in place of the new product's own
  // (or adds one where it has none). On the landing product it sits under
  // Color as before. On any other robe its core colours take the Color row's
  // place, so both rows look the same everywhere, and the robe's own colours
  // move underneath as "USA Color" (hidden when there's nothing to choose, or
  // when the row already splits them, like the camos).
  function carryRow(oldRoot, newRoot) {
    if (!familyRow) {
      var landing = qs(oldRoot, '[data-pdp-v1-styles]');
      if (landing) familyRow = landing.cloneNode(true);
    }
    if (!familyRow) return;
    var picker = qs(newRoot, '[data-pdp-v1-picker]');
    if (!picker) return;
    qsa(newRoot, '[data-pdp-v1-styles]').forEach(function (r) { r.remove(); });
    var row = familyRow.cloneNode(true);
    var color = qs(picker, '.pdp-v1__option[data-option-kind="color"]');
    var core = qs(row, '[data-pdp-v1-core]');
    var away = currentHandle !== row.getAttribute('data-origin-handle');
    if (core) core.hidden = !away;
    if (!color) { picker.insertBefore(row, picker.firstChild); return; }
    if (!core || !away) { color.after(row); return; }
    // First in the picker, as on the landing robe (The Hef lists Size first).
    picker.insertBefore(row, picker.firstChild);
    row.after(color);
    var links = qsa(row, '.pdp-v1__style');
    var own = function (h) { return links.find(function (a) { return a.getAttribute('data-style-handle') === h; }); };
    var swatch = own(currentHandle) || own(ringHandle);
    var name = qs(color, '.pdp-v1__option-name');
    if (swatch && name && name.firstChild && name.firstChild.nodeType === 3) {
      name.firstChild.textContent = swatch.title + ' ' + name.firstChild.textContent.trim();
    }
    var choices = qsa(color, '.pdp-v1__values > input[type=radio], .pdp-v1__values > a').length;
    var split = links.some(function (a) { return a.hasAttribute('data-style-color') && a.getAttribute('data-style-handle') === currentHandle; });
    color.hidden = choices < 2 || split;
  }

  // Ring the swatch for the product on screen (for a product split into
  // colour swatches, the selected colour's), and show the landing product's
  // own swatch once the shopper has left it. A product with no swatch of its
  // own (UFC Grey, reached from UFC Navy's Color row) keeps the last ring.
  function markRow(root) {
    var row = qs(root, '[data-pdp-v1-styles]');
    if (!row || !currentHandle) return;
    var origin = row.getAttribute('data-origin-handle');
    var links = qsa(row, '.pdp-v1__style');
    var listed = links.some(function (a) { return a.getAttribute('data-style-handle') === currentHandle; });
    if (listed || currentHandle === origin) ringHandle = currentHandle;
    var self = qs(row, '[data-style-self]');
    if (self) self.hidden = currentHandle === origin;
    var checked = qs(root, '.pdp-v1__option[data-option-kind="color"] input:checked');
    var colour = checked ? checked.value : null;
    links.forEach(function (a) {
      var on = a.getAttribute('data-style-handle') === ringHandle &&
        (!a.hasAttribute('data-style-color') || ringHandle !== currentHandle || a.getAttribute('data-style-color') === colour);
      a.classList.toggle('is-current', on);
      if (on) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
  }

  // The same per-block app content on every robe (Judge.me's badge: one
  // review group) keeps the rendered node, since the app won't render a new one.
  function keepAppBlocks(oldRoot, newRoot) {
    qsa(newRoot, '.shopify-app-block').forEach(function (fresh) {
      if (!qs(fresh, '.jdgm-preview-badge')) return;
      var old = fresh.id && document.getElementById(fresh.id);
      if (old && oldRoot.contains(old)) fresh.replaceWith(old);
    });
  }

  function trackView(view) {
    if (!view) return;
    document.title = view.title;
    var money = function (n) { return Number(n || 0).toFixed(2); };
    try {
      window.ElevarDataLayer = window.ElevarDataLayer || [];
      window.ElevarDataLayer.push({
        event: 'dl_view_item',
        ecommerce: {
          currencyCode: view.currencyCode,
          detail: {
            actionField: { list: '' },
            products: [{
              id: view.id, name: view.name, brand: view.brand, category: view.category,
              variant: view.variant, price: money(view.price), list: '',
              product_id: view.productId, variant_id: view.variantId,
              compare_at_price: money(view.compareAtPrice), image: view.image
            }]
          }
        }
      });
    } catch (e) {}
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('pdp_style_swap', { handle: view.handle, productId: view.productId, variantId: view.variantId });
      }
    } catch (e) {}
  }

  // Theme scripts that only set themselves up on DOMContentLoaded.
  function setUpLegacy(root) {
    if (typeof VideoCarousel === 'function') {
      qsa(root, '.video-carousel-container').forEach(function (c) { try { new VideoCarousel(c); } catch (e) {} });
    }
    var mobile = window.innerWidth <= 767;
    qsa(root, '.shop-product-title[data-font-size][data-mobile-font-size]').forEach(function (t) {
      t.style.setProperty('font-size', t.getAttribute(mobile ? 'data-mobile-font-size' : 'data-font-size') + 'px', 'important');
    });
  }

  function swapIn(oldRoot, newRoot, focusAfter) {
    var section = oldRoot.parentNode;
    carryRow(oldRoot, newRoot);
    keepAppBlocks(oldRoot, newRoot);
    var inst = oldRoot.__pdpV1;
    if (inst) {
      inst.destroy();
      instances = instances.filter(function (x) { return x !== inst; });
    }
    qsa(oldRoot, 'video').forEach(function (v) { try { v.pause(); } catch (e) {} });
    oldRoot.replaceWith(newRoot);
    // pdp-v1 and the gallery set themselves up on this (see below), and so do
    // apps that support the theme editor (the POD personalizer).
    section.dispatchEvent(new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: { sectionId: newRoot.getAttribute('data-section-id') }
    }));
    setUpLegacy(newRoot);
    markRow(newRoot);
    if (focusAfter) {
      var cur = qs(newRoot, '[data-pdp-v1-styles] .is-current') || qs(newRoot, '.pdp-v1__intro h1');
      if (cur) cur.focus({ preventScroll: true });
    }
  }

  function swapTo(oldRoot, url, push, keepFocus) {
    if (swapping) return;
    swapping = true;
    var sectionId = oldRoot.getAttribute('data-section-id');
    oldRoot.classList.add('is-swapping');
    if (!currentHandle) currentHandle = handleFromPath(window.location.pathname);
    fetchSection(url, sectionId)
      .then(function (html) {
        var newRoot = parseSection(html, sectionId);
        if (!newRoot) throw new Error('not swappable');
        if (push) {
          // Mark the landing entry so Back to it swaps too.
          if (!(history.state && history.state.pdpStyle)) {
            history.replaceState(Object.assign({}, history.state, { pdpStyle: true }), '');
          }
          history.pushState({ pdpStyle: true }, '', url.pathname + url.search);
        }
        currentHandle = handleFromPath(url.pathname);
        var run = function () { swapIn(oldRoot, newRoot, keepFocus); };
        if (document.startViewTransition && !reduceMotion.matches) document.startViewTransition(run);
        else run();
        trackView(readJson(newRoot, '[data-pdp-v1-view]'));
      })
      .catch(function (err) {
        if (window.console) console.warn('[pdp-v1] style swap fell back to a page load', err);
        window.location.href = url.pathname + url.search;
      })
      .then(function () { swapping = false; });
  }

  function swapLink(target) {
    var link = target.closest && target.closest('a[data-style-swap]');
    var root = link && link.closest('[data-pdp-v1]');
    return root && root.__pdpV1 && window.DOMParser && window.history && history.pushState ? link : null;
  }

  // Bubble phase: the picker's own handler has already added ?size= to the link.
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var link = swapLink(e.target);
    if (!link) return;
    e.preventDefault();
    if (!currentHandle) currentHandle = handleFromPath(window.location.pathname);
    if (link.getAttribute('data-style-handle') === currentHandle) {
      // A colour of the robe already on screen (Green / Desert Camo): pick it
      // in the Color radios rather than reloading the section.
      var want = link.getAttribute('data-style-color');
      var radio = want && qsa(link.closest('[data-pdp-v1]'), '.pdp-v1__option[data-option-kind="color"] input[type=radio]')
        .find(function (i) { return i.value === want; });
      if (radio && !radio.checked) radio.click();
      return;
    }
    // Keyboard users keep their place: focus moves to the same swatch in the new row.
    swapTo(link.closest('[data-pdp-v1]'), linkUrl(link), true, link.matches(':focus-visible'));
  });

  // Start fetching as soon as the shopper points at or touches a swatch.
  var prefetch = function (e) {
    var link = swapLink(e.target);
    if (!link) return;
    var root = link.closest('[data-pdp-v1]');
    fetchSection(new URL(link.href, window.location.origin), root.getAttribute('data-section-id')).catch(function () {});
  };
  document.addEventListener('pointerover', prefetch, { passive: true });
  document.addEventListener('touchstart', prefetch, { passive: true });
  document.addEventListener('focusin', prefetch);

  // Picking a colour on a split product (Green / Desert Camo) moves the ring.
  document.addEventListener('change', function (e) {
    if (!familyRow || !e.target.closest) return;
    var root = e.target.closest('[data-pdp-v1]');
    if (root && e.target.closest('[data-pdp-v1-picker]')) markRow(root);
  });

  window.addEventListener('popstate', function (e) {
    if (!e.state || !e.state.pdpStyle) return;
    var root = document.querySelector('[data-pdp-v1]');
    var handle = handleFromPath(window.location.pathname);
    if (!root || !handle) { window.location.reload(); return; }
    if (handle === currentHandle) return;
    swapTo(root, new URL(window.location.href), false);
  });

  // "Purchase area on this page" tiles and other same-page links.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-pdp-v1-scroll="purchase"]');
    if (!link) return;
    var buy = document.querySelector('[data-pdp-v1] .pdp-v1__buy');
    if (!buy) return; // No V1 purchase area on this page: follow the href.
    e.preventDefault();
    scrollToEl(buy);
    var focusTarget = buy.querySelector('.pdp-v1__option input:checked') || buy.querySelector('.pdp-v1__option input') || buy;
    focusTarget.focus({ preventScroll: true });
  });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    if (window.ProductGallery && typeof window.ProductGallery.initAll === 'function') window.ProductGallery.initAll();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    qsa(e.target, '[data-pdp-v1]').forEach(function (root) {
      if (root.__pdpV1) {
        var inst = root.__pdpV1;
        inst.destroy();
        instances = instances.filter(function (x) { return x !== inst; });
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
